// useDirtyFormReloadGuard — reload-warning safety net for dirty forms
// per frontend-design §9 G-FE-1. Wires the browser's beforeunload
// confirmation dialog to a dirty-flag boolean. The browser shows its
// generic "Changes you made may not be saved" prompt; the dialog text
// is no longer customizable in modern Chrome / Firefox / Safari (the
// `returnValue` assignment is required only to opt-in to the prompt).
//
// Used by every form whose state is dirty-tracked (long forms +
// drawer forms). The in-app dirty-discard guard inside
// `Modal.canClose` continues to handle in-app navigation; this hook
// covers browser-level reload, tab-close, and back-forward navigation.

import { useEffect } from 'react';

export function useDirtyFormReloadGuard(dirty: boolean): void {
  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Required by some browsers (Chrome <120, older Firefox) to
      // actually show the prompt; modern engines ignore the value
      // and render their own copy.
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);
}
